import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, apiErr } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  LayoutDashboard, LogOut, Percent, DollarSign, ShoppingBag, TrendingUp,
  Plus, ExternalLink, Loader2, RefreshCw, Users, UtensilsCrossed, CreditCard, Wallet, Code2, Copy, Pencil, Ban, Power, Trash2,
  Inbox, Mail, MailOpen, Reply, MapPin,
} from "lucide-react";

import MenuManager from "@/components/owner/MenuManager";
import DeliveryZones from "@/components/owner/DeliveryZones";

const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const API_BASE = process.env.REACT_APP_BACKEND_URL;

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "ahora";
  const m = Math.floor(s / 60);
  if (m < 60) return `hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h} h`;
  return `hace ${Math.floor(h / 24)} d`;
}

const STATUS_META = {
  pending: { label: "Pendiente", tint: "bg-amber-50 border-amber-200/70", badge: "bg-amber-500" },
  confirmed: { label: "Confirmado", tint: "bg-blue-50 border-blue-200/70", badge: "bg-blue-500" },
  preparing: { label: "En cocina", tint: "bg-emerald-50 border-emerald-200/70", badge: "bg-emerald-500" },
  ready: { label: "Listo", tint: "bg-violet-50 border-violet-200/70", badge: "bg-violet-500" },
  completed: { label: "Servido", tint: "bg-rose-50 border-rose-200/70", badge: "bg-rose-500" },
  cancelled: { label: "Cancelado", tint: "bg-neutral-50 border-neutral-200", badge: "bg-neutral-400" },
};

const TABS = [
  { key: "all", label: "Todas", match: () => true },
  { key: "kitchen", label: "En cocina", match: (s) => ["confirmed", "preparing"].includes(s) },
  { key: "ready", label: "Listos", match: (s) => s === "ready" },
  { key: "served", label: "Servidos", match: (s) => s === "completed" },
  { key: "cancelled", label: "Cancelados", match: (s) => s === "cancelled" },
];

const NAV = [
  { key: "inicio", label: "Inicio", icon: LayoutDashboard },
  { key: "orden", label: "Orden", icon: ShoppingBag },
  { key: "reporte", label: "Reporte", icon: TrendingUp },
  { key: "cuentas", label: "Cuentas", icon: Users },
];

function OrderLine({ orders, variant = "row" }) {
  const [tab, setTab] = useState("all");
  const counts = useMemo(() => {
    const c = {};
    TABS.forEach((t) => { c[t.key] = orders.filter((o) => t.match(o.status)).length; });
    return c;
  }, [orders]);
  const active = TABS.find((t) => t.key === tab) || TABS[0];
  const shown = orders.filter((o) => active.match(o.status));

  return (
    <section className="mb-8">
      <h2 className="font-admin font-black text-2xl text-neutral-900 mb-3">Order Line</h2>
      <div className="flex gap-6 border-b border-neutral-200 mb-5 overflow-x-auto no-scrollbar">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`relative pb-3 text-sm font-medium whitespace-nowrap transition-colors ${tab === t.key ? "text-neutral-900" : "text-neutral-400 hover:text-neutral-600"}`}
            data-testid={`orderline-tab-${t.key}`}>
            {t.label} <span className="text-neutral-400">({String(counts[t.key]).padStart(2, "0")})</span>
            {tab === t.key && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-primary rounded-full" />}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-neutral-200 bg-white py-10 text-center text-neutral-400 text-sm">
          No hay órdenes en esta vista todavía.
        </div>
      ) : variant === "column" ? (
        <div className="flex flex-col gap-3" data-testid="orderline-column">
          {/* header row */}
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 text-xs uppercase tracking-wider text-neutral-400 font-medium">
            <div className="col-span-2">Pedido</div>
            <div className="col-span-3">Negocio</div>
            <div className="col-span-3">Cliente</div>
            <div className="col-span-2 text-right">Total compra</div>
            <div className="col-span-2 text-right">Comisión</div>
          </div>
          <AnimatePresence initial={false}>
            {shown.map((o) => {
              const meta = STATUS_META[o.status] || STATUS_META.pending;
              return (
                <motion.div key={o.id} layout
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className={`grid grid-cols-2 md:grid-cols-12 gap-x-4 gap-y-1 items-center rounded-2xl border p-5 ${meta.tint}`}
                  data-testid={`orderline-card-${o.id}`}>
                  <div className="md:col-span-2">
                    <div className="font-admin font-bold text-neutral-900">#{o.id.slice(0, 6).toUpperCase()}</div>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className={`w-1.5 h-1.5 rounded-full ${meta.badge}`} />
                      <span className="text-xs text-neutral-500">{meta.label}</span>
                    </div>
                  </div>
                  <div className="md:col-span-3">
                    <div className="md:hidden text-xs text-neutral-400 uppercase tracking-wide">Negocio</div>
                    <div className="text-sm font-medium text-neutral-800 truncate">{o.restaurant_name || "—"}</div>
                    <div className="text-xs text-neutral-400">{timeAgo(o.created_at)} · {o.items_count} items</div>
                  </div>
                  <div className="md:col-span-3">
                    <div className="md:hidden text-xs text-neutral-400 uppercase tracking-wide">Cliente</div>
                    <div className="text-sm text-neutral-700 truncate">{o.customer_name || "—"}</div>
                  </div>
                  <div className="md:col-span-2 md:text-right">
                    <div className="md:hidden text-xs text-neutral-400 uppercase tracking-wide">Total</div>
                    <div className="text-sm font-semibold tabular-nums text-neutral-900">{money(o.total)}</div>
                  </div>
                  <div className="md:col-span-2 md:text-right">
                    <div className="md:hidden text-xs text-neutral-400 uppercase tracking-wide">Comisión</div>
                    <div className="text-sm font-semibold tabular-nums text-emerald-600">{money(o.commission)}</div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">
          <AnimatePresence initial={false}>
            {shown.map((o) => {
              const meta = STATUS_META[o.status] || STATUS_META.pending;
              return (
                <motion.div key={o.id} layout
                  initial={{ opacity: 0, y: -14, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className={`min-w-[250px] rounded-2xl border p-5 ${meta.tint}`}
                  data-testid={`orderline-card-${o.id}`}>
                  <div className="font-admin font-bold text-neutral-900">Pedido #{o.id.slice(0, 6).toUpperCase()}</div>
                  <div className="flex gap-6 mt-3 text-sm text-neutral-600">
                    <span className="truncate max-w-[120px]">{o.restaurant_name}</span>
                    <span className="whitespace-nowrap">Items: {o.items_count}x</span>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-sm text-neutral-400">{timeAgo(o.created_at)}</span>
                    <span className={`text-white text-xs font-medium px-3 py-1.5 rounded-full ${meta.badge}`}>{meta.label}</span>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </section>
  );
}

function Metric({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className="rounded-2xl border border-neutral-200/80 bg-white p-5 shadow-sm shadow-neutral-100" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-neutral-500 font-medium">{label}</span>
        <Icon className={`w-4 h-4 ${accent || "text-neutral-400"}`} />
      </div>
      <div className="mt-3 font-admin font-medium tracking-tight text-2xl tabular-nums text-neutral-900">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", owner_email: "", owner_password: "", owner_name: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState(null);
  const [custLoading, setCustLoading] = useState(false);
  const [appConfig, setAppConfig] = useState(null);
  const [editResto, setEditResto] = useState(null);
  const [menuResto, setMenuResto] = useState(null);
  const [zonesResto, setZonesResto] = useState(null);
  const [deleteResto, setDeleteResto] = useState(null);
  const [inbox, setInbox] = useState(null);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [view, setView] = useState("inicio");

  const markRead = async (m, read) => {
    setInbox((prev) => prev && { ...prev, unread: prev.unread + (read ? -1 : 1), messages: prev.messages.map((x) => x.id === m.id ? { ...x, read } : x) });
    try { await api.put(`/admin/contact-messages/${m.id}`, { read }); } catch (e) { toast.error(apiErr(e)); loadInbox(); }
  };
  const delMsg = async (id) => {
    try { await api.delete(`/admin/contact-messages/${id}`); toast.success("Mensaje eliminado"); loadInbox(); } catch (e) { toast.error(apiErr(e)); }
  };
  const loadInbox = async () => {
    try { const { data } = await api.get("/admin/contact-messages"); setInbox(data); } catch (e) { /* silent */ }
  };
  const openInbox = () => {
    setInboxOpen(true);
    (inbox?.messages || []).filter((m) => !m.read).forEach((m) => markRead(m, true));
  };

  const toggleActive = async (r) => {
    try {
      await api.put(`/admin/restaurants/${r.id}`, { active: !r.active });
      toast.success(r.active ? "Restaurante bloqueado" : "Restaurante activado");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/restaurants/${deleteResto.id}`);
      toast.success("Restaurante eliminado");
      setDeleteResto(null);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const copy = (text, label) => {
    if (navigator.clipboard) navigator.clipboard.writeText(text);
    toast.success(`${label} copiado`);
  };

  const saveEdit = async () => {
    if (!editResto.name.trim()) { toast.error("El nombre no puede estar vacío"); return; }
    try {
      await api.put(`/admin/restaurants/${editResto.id}`, { name: editResto.name });
      toast.success("Nombre actualizado");
      setEditResto(null);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const load = async () => {
    try {
      const [o, ord] = await Promise.all([api.get("/admin/overview"), api.get("/admin/orders")]);
      setData(o.data);
      setOrders(ord.data);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  useEffect(() => {
    load();
    loadInbox();
    const t = setInterval(() => { load(); loadInbox(); }, 5000);
    return () => clearInterval(t);
  }, []);

  const createRestaurant = async () => {
    setSaving(true);
    try {
      await api.post("/admin/restaurants", form);
      toast.success("Restaurante creado");
      setOpen(false);
      setForm({ name: "", owner_email: "", owner_password: "", owner_name: "", description: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const openCustomers = async (r) => {
    setCustLoading(true);
    setCustomers({ restaurant_name: r.name, customers: null });
    try {
      const { data } = await api.get(`/admin/restaurants/${r.id}/customers`);
      setCustomers(data);
    } catch (e) { toast.error(apiErr(e)); setCustomers(null); } finally { setCustLoading(false); }
  };

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center bg-neutral-50"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const rate = Math.round((data.commission_rate || 0.07) * 100);
  const axis = { fontSize: 11, fill: "#9ca3af" };

  return (
    <div className="min-h-screen bg-neutral-50 font-adminbody text-neutral-900 flex">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 bg-white border-r border-neutral-200 flex flex-col sticky top-0 h-screen">
        <div className="h-16 flex items-center px-6 border-b border-neutral-100">
          <img src="/domifood-logo.png" alt="Domifood" className="h-7 w-auto" />
        </div>
        <nav className="flex-1 p-3 space-y-1" data-testid="admin-sidebar">
          {NAV.map((n) => (
            <button key={n.key} onClick={() => setView(n.key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${view === n.key ? "bg-neutral-900 text-white" : "text-neutral-600 hover:bg-neutral-100"}`}
              data-testid={`nav-${n.key}`}>
              <n.icon className="w-4 h-4" /> {n.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-neutral-100">
          <button onClick={() => { logout(); nav("/login"); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-neutral-600 hover:bg-neutral-100" data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4" /> Salir
          </button>
        </div>
      </aside>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 bg-white/85 backdrop-blur-xl border-b border-neutral-200">
          <div className="w-full px-6 lg:px-10 h-16 flex items-center justify-between">
            <div>
              <div className="font-admin font-bold leading-none">{NAV.find((n) => n.key === view)?.label}</div>
              <div className="text-xs text-neutral-500">{user?.email}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1.5 text-xs text-emerald-600 bg-emerald-50 rounded-full px-3 py-1 mr-1">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> En vivo
              </span>
              <Button variant="outline" size="sm" onClick={openInbox} className="relative" data-testid="admin-inbox-btn">
                <Inbox className="w-4 h-4 mr-1" /> Buzón
                {inbox?.unread > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center" data-testid="inbox-unread-badge">{inbox.unread}</span>
                )}
              </Button>
              <Button variant="ghost" size="icon" onClick={load} data-testid="admin-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
            </div>
          </div>
        </header>

        <main className="w-full px-6 lg:px-10 py-8">
          {/* INICIO — resumen de ventas + métricas */}
          {view === "inicio" && (
            <>
              <div className="mb-4">
                <h2 className="font-admin font-black text-xl">Resumen de ventas</h2>
                <p className="text-sm text-neutral-500">Datos consolidados de todos los restaurantes · comisión {rate}%</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
                <Metric icon={DollarSign} label="Ventas totales" value={money(data.total_sales)} accent="text-primary" testid="metric-sales" />
                <Metric icon={Percent} label={`Comisión bruta ${rate}%`} value={money(data.total_commission)} accent="text-neutral-400" testid="metric-commission" />
                <Metric icon={CreditCard} label={`Fees Stripe ${(data.stripe_fee_percent * 100).toFixed(1)}%`} value={`-${money(data.total_stripe_fees)}`} accent="text-blue-500" testid="metric-stripe-fees" />
                <Metric icon={Wallet} label="Mi ganancia neta" value={money(data.platform_net)} accent="text-emerald-500" testid="metric-platform-net" />
                <Metric icon={TrendingUp} label="Neto a restaurantes" value={money(data.total_net)} testid="metric-net" />
                <Metric icon={ShoppingBag} label="Pedidos pagados" value={data.total_orders} testid="metric-orders" />
              </div>
              <OrderLine orders={orders} />
            </>
          )}

          {/* ORDEN — feed de pedidos en vivo */}
          {view === "orden" && <OrderLine orders={orders} variant="column" />}

          {/* REPORTE — gráficos financieros */}
          {view === "reporte" && (
            <div className="grid lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 rounded-2xl border border-neutral-200/80 bg-white p-5 shadow-sm shadow-neutral-100">
                <h3 className="font-admin font-bold mb-4">Ventas y comisión (14 días)</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={data.timeseries}>
                    <defs>
                      <linearGradient id="gs" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(0 0% 9%)" stopOpacity={0.28} />
                        <stop offset="100%" stopColor="hsl(0 0% 9%)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="date" tick={axis} tickFormatter={(d) => d?.slice(5)} />
                    <YAxis tick={axis} />
                    <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 10, border: "1px solid #eee" }} />
                    <Area type="monotone" dataKey="sales" stroke="hsl(0 0% 9%)" fill="url(#gs)" strokeWidth={2} name="Ventas" />
                    <Area type="monotone" dataKey="commission" stroke="hsl(142 71% 45%)" fill="transparent" strokeWidth={2} name="Comisión" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="rounded-2xl border border-neutral-200/80 bg-white p-5 shadow-sm shadow-neutral-100">
                <h3 className="font-admin font-bold mb-4">Ventas por restaurante</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.by_restaurant} layout="vertical" margin={{ left: 10 }}>
                    <XAxis type="number" tick={axis} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#9ca3af" }} width={90} />
                    <Tooltip formatter={(v) => money(v)} cursor={{ fill: "#00000008" }} contentStyle={{ borderRadius: 10, border: "1px solid #eee" }} />
                    <Bar dataKey="sales" fill="hsl(0 0% 9%)" radius={[0, 4, 4, 0]} name="Ventas" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* CUENTAS — gestión de restaurantes */}
          {view === "cuentas" && (
          <div className="rounded-2xl border border-neutral-200/80 bg-white shadow-sm shadow-neutral-100">
          <div className="flex items-center justify-between p-5 border-b border-neutral-100">
            <h3 className="font-admin font-bold">Restaurantes ({data.by_restaurant.length})</h3>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="add-restaurant-btn"><Plus className="w-4 h-4 mr-1" /> Nuevo restaurante</Button>
              </DialogTrigger>
              <DialogContent className="font-adminbody">
                <DialogHeader>
                  <DialogTitle className="font-admin">Crear restaurante y propietario</DialogTitle>
                  <DialogDescription>Se creará el negocio y una cuenta de propietario para gestionarlo.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="space-y-1.5"><Label>Nombre del restaurante</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-resto-name" /></div>
                  <div className="space-y-1.5"><Label>Descripción</Label>
                    <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="new-resto-desc" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Nombre propietario</Label>
                      <Input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} data-testid="new-owner-name" /></div>
                    <div className="space-y-1.5"><Label>Email propietario</Label>
                      <Input type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} data-testid="new-owner-email" /></div>
                  </div>
                  <div className="space-y-1.5"><Label>Contraseña propietario</Label>
                    <Input type="text" value={form.owner_password} onChange={(e) => setForm({ ...form, owner_password: e.target.value })} data-testid="new-owner-password" /></div>
                </div>
                <DialogFooter>
                  <Button onClick={createRestaurant} disabled={saving} data-testid="save-restaurant-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="restaurants-table">
              <thead>
                <tr className="text-left text-neutral-500 border-b border-neutral-100">
                  <th className="px-5 py-3 font-medium">Restaurante</th>
                  <th className="px-5 py-3 font-medium text-right">Pedidos</th>
                  <th className="px-5 py-3 font-medium text-right">Ventas</th>
                  <th className="px-5 py-3 font-medium text-right">Comisión {rate}%</th>
                  <th className="px-5 py-3 font-medium text-right">Fees Stripe</th>
                  <th className="px-5 py-3 font-medium text-right">Mi ganancia</th>
                  <th className="px-5 py-3 font-medium text-right">Neto restaurante</th>
                  <th className="px-5 py-3 font-medium text-right">Clientes</th>
                  <th className="px-5 py-3 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {data.by_restaurant.map((r) => (
                  <tr key={r.id} className={`border-b border-neutral-50 hover:bg-neutral-50 transition-colors ${!r.active ? "opacity-60" : ""}`} data-testid={`resto-row-${r.slug}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{r.name}</span>
                        {!r.active && <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-600">Bloqueado</span>}
                        <button onClick={() => setEditResto({ id: r.id, name: r.name })} className="text-neutral-300 hover:text-primary transition-colors" title="Renombrar" data-testid={`rename-${r.slug}`}><Pencil className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">{r.orders}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{money(r.sales)}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-neutral-500">{money(r.commission)}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-blue-600">-{money(r.stripe_fee)}</td>
                    <td className="px-5 py-3 text-right tabular-nums font-semibold text-emerald-600">{money(r.platform_net)}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{money(r.net)}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => openCustomers(r)} className="inline-flex items-center gap-1 text-neutral-600 hover:text-primary transition-colors" data-testid={`view-customers-${r.slug}`}>
                        <Users className="w-4 h-4" /> Ver
                      </button>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-3 justify-end">
                        <button onClick={() => setMenuResto(r)} className="inline-flex items-center gap-1 text-neutral-600 hover:text-primary transition-colors" data-testid={`edit-menu-${r.slug}`}>
                          <UtensilsCrossed className="w-4 h-4" /> Menú
                        </button>
                        <button onClick={() => setZonesResto(r)} className="inline-flex items-center gap-1 text-neutral-600 hover:text-primary transition-colors" data-testid={`delivery-zones-${r.slug}`}>
                          <MapPin className="w-4 h-4" /> Zonas
                        </button>
                        <button onClick={() => setAppConfig(r)} className="inline-flex items-center gap-1 text-neutral-600 hover:text-primary transition-colors" data-testid={`app-config-${r.slug}`}>
                          <Code2 className="w-4 h-4" /> Config
                        </button>
                        <button onClick={() => toggleActive(r)} className={`inline-flex items-center gap-1 transition-colors ${r.active ? "text-neutral-600 hover:text-amber-600" : "text-emerald-600 hover:text-emerald-700"}`} data-testid={`toggle-active-${r.slug}`}>
                          {r.active ? <><Ban className="w-4 h-4" /> Bloquear</> : <><Power className="w-4 h-4" /> Activar</>}
                        </button>
                        <button onClick={() => setDeleteResto(r)} className="inline-flex items-center gap-1 text-neutral-500 hover:text-red-600 transition-colors" data-testid={`delete-${r.slug}`}>
                          <Trash2 className="w-4 h-4" /> Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
          )}
        </main>
      </div>

      {/* Customers dialog */}
      <Dialog open={!!customers} onOpenChange={(o) => !o && setCustomers(null)}>
        <DialogContent className="font-adminbody max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-admin flex items-center gap-2"><Users className="w-5 h-5 text-primary" /> Clientes · {customers?.restaurant_name}</DialogTitle>
            <DialogDescription>Clientes que han realizado pedidos en este restaurante.</DialogDescription>
          </DialogHeader>
          {custLoading || customers?.customers == null ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : customers.customers.length === 0 ? (
            <div className="py-12 text-center text-neutral-400">
              <UtensilsCrossed className="w-8 h-8 mx-auto mb-2" /> Aún no hay clientes con pedidos.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="customers-table">
              <thead>
                <tr className="text-left text-neutral-500 border-b border-neutral-100">
                  <th className="py-2 font-medium">Cliente</th>
                  <th className="py-2 font-medium">Teléfono</th>
                  <th className="py-2 font-medium text-right">Pedidos</th>
                  <th className="py-2 font-medium text-right">Gastado</th>
                  <th className="py-2 font-medium text-right">Último</th>
                </tr>
              </thead>
              <tbody>
                {customers.customers.map((c, i) => (
                  <tr key={i} className="border-b border-neutral-50" data-testid={`customer-row-${i}`}>
                    <td className="py-2.5 font-medium">{c.name}</td>
                    <td className="py-2.5 text-neutral-500">{c.phone || "—"}</td>
                    <td className="py-2.5 text-right tabular-nums">{c.paid_orders}/{c.orders}</td>
                    <td className="py-2.5 text-right tabular-nums text-emerald-600">{money(c.total_spent)}</td>
                    <td className="py-2.5 text-right text-neutral-500">{c.last_order ? c.last_order.slice(0, 10) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DialogContent>
      </Dialog>

      {/* App config dialog (per-restaurant identifier for building native apps) */}
      <Dialog open={!!appConfig} onOpenChange={(o) => !o && setAppConfig(null)}>
        <DialogContent className="font-adminbody max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-admin flex items-center gap-2"><Code2 className="w-5 h-5 text-primary" /> Configuración de app · {appConfig?.name}</DialogTitle>
            <DialogDescription>Usa estos datos al crear la app nativa de este restaurante. Todo lo que haga la app quedará asociado a este restaurante.</DialogDescription>
          </DialogHeader>
          {appConfig && (
            <div className="space-y-3">
              {[
                ["RESTAURANT_ID", appConfig.id],
                ["SLUG", appConfig.slug],
                ["API_BASE_URL", API_BASE],
                ["Menú (GET)", `${API_BASE}/api/restaurants/${appConfig.id}/menu`],
                ["Checkout (POST)", `${API_BASE}/api/payments/checkout  body: { "restaurant_id": "${appConfig.id}", "items": [...] }`],
              ].map(([label, val]) => (
                <div key={label} className="rounded-lg border border-neutral-200 bg-neutral-50 p-3">
                  <div className="text-xs uppercase tracking-wide text-neutral-500 mb-1">{label}</div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs break-all text-neutral-800">{val}</code>
                    <button onClick={() => copy(val, label)} className="text-neutral-400 hover:text-primary shrink-0" data-testid={`copy-${label}`}><Copy className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
              <p className="text-xs text-neutral-400">Cada app puede tener un diseño totalmente distinto; solo debe apuntar a este <b>RESTAURANT_ID</b> y a la <b>API_BASE_URL</b> para compartir menú, pedidos, tema y pagos.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rename restaurant dialog */}
      <Dialog open={!!editResto} onOpenChange={(o) => !o && setEditResto(null)}>
        <DialogContent className="font-adminbody">
          <DialogHeader>
            <DialogTitle className="font-admin">Renombrar restaurante</DialogTitle>
            <DialogDescription>Cambia el nombre visible del restaurante. El slug y el ID no cambian.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Nombre</Label>
            <Input value={editResto?.name || ""} onChange={(e) => setEditResto({ ...editResto, name: e.target.value })} data-testid="rename-input" />
          </div>
          <DialogFooter>
            <Button onClick={saveEdit} data-testid="rename-save-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Menu editor dialog (superadmin manages any restaurant's menu) */}
      <Dialog open={!!menuResto} onOpenChange={(o) => !o && setMenuResto(null)}>
        <DialogContent className="font-adminbody max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-admin flex items-center gap-2"><UtensilsCrossed className="w-5 h-5 text-primary" /> Menú · {menuResto?.name}</DialogTitle>
            <DialogDescription>Gestiona categorías, platos (precio, foto, descripción, disponibilidad) y modificadores de este restaurante.</DialogDescription>
          </DialogHeader>
          {menuResto && <MenuManager key={menuResto.id} restaurantId={menuResto.id} />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!zonesResto} onOpenChange={(o) => !o && setZonesResto(null)}>
        <DialogContent className="font-adminbody max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-admin flex items-center gap-2"><MapPin className="w-5 h-5" /> Zonas de delivery · {zonesResto?.name}</DialogTitle>
            <DialogDescription>Define hasta dónde llega el delivery de este negocio.</DialogDescription>
          </DialogHeader>
          {zonesResto && <DeliveryZones key={zonesResto.id} restaurantId={zonesResto.id} />}
        </DialogContent>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteResto} onOpenChange={(o) => !o && setDeleteResto(null)}>
        <DialogContent className="font-adminbody">
          <DialogHeader>
            <DialogTitle className="font-admin flex items-center gap-2"><Trash2 className="w-5 h-5 text-red-600" /> Eliminar restaurante</DialogTitle>
            <DialogDescription>
              Vas a eliminar permanentemente <b>{deleteResto?.name}</b>, junto con su menú, categorías, inventario y pedidos. Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteResto(null)} data-testid="cancel-delete-btn">Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={confirmDelete} data-testid="confirm-delete-btn">Eliminar definitivamente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact inbox dialog */}
      <Dialog open={inboxOpen} onOpenChange={setInboxOpen}>
        <DialogContent className="font-adminbody max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-admin flex items-center gap-2"><Inbox className="w-5 h-5 text-primary" /> Buzón de contacto</DialogTitle>
            <DialogDescription>Mensajes enviados desde la página de Contacto. Responde por correo o elimínalos.</DialogDescription>
          </DialogHeader>
          {!inbox ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : inbox.messages.length === 0 ? (
            <div className="py-12 text-center text-neutral-400"><Mail className="w-8 h-8 mx-auto mb-2" /> Aún no hay mensajes.</div>
          ) : (
            <div className="space-y-3" data-testid="inbox-list">
              {inbox.messages.map((m) => (
                <div key={m.id} className={`rounded-xl border p-4 ${m.read ? "border-neutral-200 bg-white" : "border-primary/30 bg-primary/5"}`} data-testid={`inbox-msg-${m.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {!m.read && <span className="w-2 h-2 rounded-full bg-primary shrink-0" />}
                        <span className="font-semibold truncate">{m.name}</span>
                        <span className="text-xs text-neutral-400 truncate">· {m.email}</span>
                      </div>
                      {m.subject && <div className="text-sm font-medium text-neutral-700 mt-1">{m.subject}</div>}
                      <p className="text-sm text-neutral-600 mt-1 whitespace-pre-wrap">{m.message}</p>
                      <div className="text-xs text-neutral-400 mt-2">{m.created_at ? new Date(m.created_at).toLocaleString("es-ES") : ""}</div>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <a href={`mailto:${m.email}?subject=${encodeURIComponent("RE: " + (m.subject || "Tu mensaje a Domifood"))}`} data-testid={`inbox-reply-${m.id}`}>
                        <Button size="sm" className="h-8"><Reply className="w-3.5 h-3.5 mr-1" /> Responder</Button>
                      </a>
                      <div className="flex items-center gap-1">
                        <button onClick={() => markRead(m, !m.read)} className="text-neutral-400 hover:text-primary p-1" title={m.read ? "Marcar como no leído" : "Marcar como leído"} data-testid={`inbox-toggle-read-${m.id}`}>
                          {m.read ? <Mail className="w-4 h-4" /> : <MailOpen className="w-4 h-4" />}
                        </button>
                        <button onClick={() => delMsg(m.id)} className="text-neutral-400 hover:text-red-600 p-1" title="Eliminar" data-testid={`inbox-delete-${m.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
